/**
 * Las guías, en español — escritas para quien cobra en México.
 *
 * Escritas directamente en español, no traducidas: tienen las mismas
 * secciones que las inglesas, pero no las mismas palabras, y donde México
 * funciona distinto (el IVA sobre los intereses, el aguinaldo, los meses sin
 * intereses) lo dicen. Cada cifra de un ejemplo se calcula en el texto que la
 * rodea, para que quien lee pueda rehacer la cuenta. Lo que depende de una ley
 * o de un banco se dice como suele ser, nunca como promesa sobre la tarjeta de
 * quien lee.
 */
import { GuideLink } from './GuideLink';
import type { Guide, GuideId } from './types';

const PUBLISHED = '2026-10-19';
const UPDATED = '2026-11-03';

const budgetByPaycheck: Guide = {
  id: 'budget-by-paycheck',
  title: 'Cómo hacer un presupuesto quincenal',
  intro:
    'Un presupuesto mensual supone que el dinero llega una vez. Si cobras el 15 y el último día del mes, conviene planear cada quincena por separado: qué tiene que cubrir antes del siguiente pago y cuánto te queda para gastar al día.',
  summary:
    'Planea cada quincena en vez del mes: qué tiene que cubrir antes del siguiente pago, cuánto apartar y cuánto puedes gastar al día.',
  published: PUBLISHED,
  updated: UPDATED,
  sections: [
    {
      id: 'why',
      title: 'Por qué el presupuesto mensual no cuadra',
      blocks: [
        {
          kind: 'p',
          content:
            'Un presupuesto mensual suma lo que entra en el mes, resta lo que sale y divide. El problema es el calendario. La renta sale el día 1, del sueldo que cayó el día anterior; el pago de la tarjeta puede caer en la otra quincena. Una quincena carga casi todos los pagos y la otra casi ninguno, y el promedio no describe a ninguna de las dos.',
        },
        {
          kind: 'p',
          content:
            'Por eso pasa lo de siempre: holgado los primeros días, apretado los últimos, y un presupuesto que dice que vas bien hasta la semana en que ya no.',
        },
      ],
    },
    {
      id: 'schedules',
      title: 'Quincenal, catorcenal o semanal',
      blocks: [
        {
          kind: 'table',
          head: ['Pago', 'Cuándo', 'Al año', 'Qué cuidar'],
          rows: [
            [
              'Quincenal',
              'El 15 y el último día del mes',
              '24',
              'Las quincenas no miden lo mismo: de 13 a 16 días. Si el pago cae en fin de semana, muchas empresas pagan el viernes anterior.',
            ],
            [
              'Catorcenal',
              'Cada 14 días, el mismo día de la semana',
              '26',
              'Casi todos los años, dos meses traen tres pagos, y cuál de ellos cubre la renta cambia de un mes a otro.',
            ],
            [
              'Semanal',
              'Cada semana',
              '52',
              'Cuatro o cinco pagos al mes, así que un pago mensual se lleva buena parte de la semana en que cae.',
            ],
          ],
        },
        {
          kind: 'p',
          content:
            'El método sirve para los tres. Un ciclo va del día en que cobras al día anterior al siguiente pago.',
        },
      ],
    },
    {
      id: 'steps',
      title: 'Seis pasos para cada quincena',
      blocks: [
        { kind: 'h3', content: '1. Parte de lo que te cae en la cuenta' },
        {
          kind: 'p',
          content:
            'Tu sueldo neto, ya sin ISR, IMSS ni otros descuentos de nómina; no el bruto de tu contrato. Si varía, usa el más bajo de tus últimos pagos.',
        },
        { kind: 'h3', content: '2. Anota lo que hay que pagar antes del siguiente pago' },
        {
          kind: 'p',
          content:
            'Recorre el calendario desde hoy hasta el día antes de tu próximo pago y anota todo lo que cae en ese periodo: renta o hipoteca, agua, gas, internet y celular, seguros, el crédito del coche, suscripciones, y el pago de cada tarjeta cuya fecha límite caiga dentro.',
        },
        {
          kind: 'p',
          content:
            'Asigna cada pago a la quincena con la que lo vas a pagar, no al mes al que pertenece: la renta del día 1 se paga con la quincena anterior. La luz suele llegar cada dos meses, y el predial o el seguro del coche una vez al año; esos rinden más si apartas un poco cada quincena — lo anual, entre 24.',
        },
        { kind: 'h3', content: '3. Decide cuánto pagar de cada tarjeta' },
        {
          kind: 'p',
          content: (
            <>
              Si puedes, el pago para no generar intereses: es el que evita intereses. El pago
              mínimo deja tu tarjeta al corriente, pero cobra intereses, más IVA, sobre lo que
              queda. La diferencia está en{' '}
              <GuideLink to="statement-and-due-dates">
                Fecha de corte y fecha límite de pago
              </GuideLink>
              .
            </>
          ),
        },
        { kind: 'h3', content: '4. Aparta tu ahorro primero' },
        {
          kind: 'p',
          content:
            'Lo que piensas ahorrar, muévelo el día que cobras, antes de gastar nada, aunque sea poco. Lo que sobra al final de la quincena casi nunca se ahorra; lo que sale el primer día, casi siempre.',
        },
        { kind: 'h3', content: '5. Divide lo que queda entre los días hasta el siguiente pago' },
        {
          kind: 'p',
          content:
            'Eso es lo que puedes gastar al día en todo lo que decides sobre la marcha: súper, transporte, gasolina, comidas fuera. Es el número que vale la pena revisar.',
        },
        { kind: 'h3', content: '6. Revísalo a media quincena' },
        {
          kind: 'p',
          content:
            'A la mitad, divide lo que de verdad queda entre los días que de verdad faltan. Si salió menos de lo planeado, el resto de la quincena se ajusta un poco cada día, en vez de todo de golpe en los últimos dos.',
        },
      ],
    },
    {
      id: 'example',
      title: 'Un ejemplo',
      blocks: [
        {
          kind: 'p',
          content:
            'Un sueldo neto de $18,000 al mes: $9,000 cada quincena. Cobras el 15 de un mes de 30 días, así que hasta el siguiente pago hay 15 días. Lo que vence antes:',
        },
        {
          kind: 'table',
          head: ['Quincena del 15', 'Monto'],
          rows: [
            ['Lo que te cae', '$9,000'],
            ['Pago de la tarjeta (para no generar intereses)', '−$2,400'],
            ['Internet y celular', '−$750'],
            ['Suscripciones', '−$350'],
            ['Ahorro, apartado el día de pago', '−$1,000'],
            ['Libre para el día a día', '$4,500'],
            ['Por día, durante 15 días', '$300'],
          ],
        },
        {
          kind: 'p',
          content:
            'La quincena del 30 carga la renta: $5,000. Con el mismo ahorro te quedan $3,000 para 15 días, $200 al día. Ninguna de las dos es una quincena mala; una carga la renta, y saberlo antes de que empiece es justo para lo que sirve planear por quincena.',
        },
        {
          kind: 'callout',
          content:
            'Si la diferencia te pesa, aparta parte de la renta en la quincena más holgada. Con $750 del 15 guardados para la renta, las dos te dejan $3,750: $250 al día, cada quincena.',
        },
      ],
    },
    {
      id: 'extra-pay',
      title: 'Lo que no llega cada quincena',
      blocks: [
        {
          kind: 'p',
          content:
            'El aguinaldo — al menos quince días de salario, antes del 20 de diciembre — y el reparto de utilidades, normalmente en mayo, no llegan cada quincena. No los cuentes en el presupuesto de todos los meses: decide a qué van antes de que lleguen, sea una deuda, un ahorro o los gastos de enero.',
        },
        {
          kind: 'p',
          content:
            'Si cobras cada catorce días, recibes 26 pagos al año, no 24. Planea cada mes con dos y el tercero llega sin nada asignado: el dinero más fácil del año para ahorrar o para bajar una deuda.',
        },
      ],
    },
    {
      id: 'irregular',
      title: 'Si tu ingreso varía',
      blocks: [
        {
          kind: 'p',
          content:
            'Si trabajas por tu cuenta, por comisiones o con propinas, planea con lo que ya llegó, no con lo que esperas. Cubre primero los pagos del siguiente ciclo y trata lo que sobre como dinero del ciclo siguiente. Un piso — lo menos que has ganado en un mes reciente — da un plan que se cumple; un promedio da uno optimista.',
        },
        {
          kind: 'p',
          content:
            'Si facturas, aparta lo de los impuestos el día que te pagan. Nunca fue tuyo para gastar, y la declaración mensual es mucho más fácil con dinero que ya está guardado.',
        },
      ],
    },
    {
      id: 'twocoins',
      title: 'Cómo lo hace TwoCoins',
      blocks: [
        {
          kind: 'p',
          content:
            'TwoCoins planea por el ciclo en que cobras: quincena, catorcena, semana, mes o un rango que tú eliges. Parte de lo que tienes en tus cuentas, le quita lo que deben tus tarjetas y cada pago que cae antes de tu siguiente quincena, le suma lo que todavía te entra, y divide lo que queda entre los días: el paso cinco, hecho con tu saldo real y al día conforme anotas lo que gastas. Si un pago cae antes que el sueldo que lo cubre, te lo dice con la fecha.',
        },
        {
          kind: 'p',
          content:
            'Nunca te pide la contraseña de tu banco: anotas cada gasto, o eliges una tienda que ya usaste.',
        },
      ],
    },
  ],
};

const statementAndDueDates: Guide = {
  id: 'statement-and-due-dates',
  title: 'Fecha de corte y fecha límite de pago',
  intro:
    'Tu tarjeta de crédito tiene dos fechas al mes. La fecha de corte decide en qué estado de cuenta cae una compra; la fecha límite de pago, hasta cuándo tienes para pagarlo. Conocer las dos es la diferencia entre no pagar intereses y pagarlos cada mes.',
  summary:
    'Qué son la fecha de corte y la fecha límite de pago, cuál pago evita intereses y cuál es el mejor día para una compra grande.',
  published: PUBLISHED,
  updated: UPDATED,
  sections: [
    {
      id: 'two-dates',
      title: 'Las dos fechas',
      blocks: [
        {
          kind: 'dl',
          items: [
            {
              term: 'Fecha de corte',
              description:
                'El último día del periodo. Lo que compras hasta ese día entra en este estado de cuenta; lo que compras después, en el siguiente. Ese día queda fijo cuánto debes de ese periodo.',
            },
            {
              term: 'Fecha límite de pago',
              description:
                'El último día para pagar ese estado de cuenta sin recargos. Suele caer unos 20 días después del corte, y viene en tu estado de cuenta y en la app de tu banco.',
            },
          ],
        },
        {
          kind: 'p',
          content:
            'Si cubres el pago para no generar intereses antes de la fecha límite, las compras de ese periodo no te cuestan intereses.',
        },
      ],
    },
    {
      id: 'which-amount',
      title: 'Qué pago hacer',
      blocks: [
        {
          kind: 'table',
          head: ['Pago', 'Qué es', 'Si lo pagas'],
          rows: [
            [
              'Pago para no generar intereses',
              'Lo que compraste en el periodo, más la mensualidad del mes de cada compra a meses sin intereses.',
              'No pagas intereses. Es el que conviene.',
            ],
            [
              'Pago mínimo',
              'Una parte pequeña de lo que debes, que fija el banco.',
              'Tu tarjeta queda al corriente, pero pagas intereses, más IVA, sobre lo que dejas pendiente.',
            ],
            [
              'Saldo total',
              'Todo lo que debes, incluido lo que falta de tus compras a meses.',
              'Liquidas la tarjeta, mensualidades futuras incluidas. No hace falta para evitar intereses.',
            ],
          ],
        },
        {
          kind: 'p',
          content:
            'Lo que cuesta el mínimo es fácil de calcular. Con una tasa de 48% anual, el interés es de 4% al mes: $10,000 que dejas pendientes cuestan $400 de intereses al mes siguiente, más $64 de IVA — y otra vez el mes que sigue, si siguen ahí.',
        },
        {
          kind: 'callout',
          content:
            'El pago mínimo no es una sugerencia de cuánto pagar. Es lo menos que el banco acepta para no marcarte como atrasado.',
        },
      ],
    },
    {
      id: 'best-day',
      title: 'El mejor día para una compra grande',
      blocks: [
        {
          kind: 'p',
          content:
            'El día después de tu corte. La compra entra en el siguiente estado de cuenta, que corta un mes después y se paga unos 20 días más tarde. Con una tarjeta que corta el 12 y se paga el 2 del mes siguiente:',
        },
        {
          kind: 'table',
          head: ['Compras el', 'Entra en el corte del', 'Pagas antes del', 'Días para pagar'],
          rows: [
            ['11 de septiembre', '12 de septiembre', '2 de octubre', '21'],
            ['13 de septiembre', '12 de octubre', '2 de noviembre', '50'],
          ],
        },
        {
          kind: 'p',
          content:
            'Dos días de diferencia, un mes más para pagar. Las compras cuentan por la fecha en que el banco las registra, que puede ser uno o dos días después; por eso el día después del corte es seguro y el mismo día del corte no.',
        },
        {
          kind: 'p',
          content:
            'Es aire, no dinero gratis: solo funciona si cubres completo el pago para no generar intereses cuando llega el estado de cuenta.',
        },
      ],
    },
    {
      id: 'credit-score',
      title: 'Tu historial en el Buró de Crédito',
      blocks: [
        {
          kind: 'p',
          content:
            'Lo que más pesa en tu historial es pagar a tiempo. Pagar al menos el mínimo antes de la fecha límite evita el atraso; cubrir el pago para no generar intereses evita además los intereses. Usar una parte pequeña de tu línea de crédito también cuenta a tu favor.',
        },
        {
          kind: 'p',
          content:
            'Si pagas tarde, el banco cobra una comisión por pago tardío, más IVA, además de los intereses, y el atraso puede reportarse al buró.',
        },
      ],
    },
    {
      id: 'several-cards',
      title: 'Con más de una tarjeta',
      blocks: [
        {
          kind: 'p',
          content:
            'Cada tarjeta tiene su propio corte, así que casi siempre hay una que acaba de cortar: es la que te da más días para pagar lo que compres hoy.',
        },
        {
          kind: 'p',
          content:
            'Algunos bancos te dejan cambiar tu fecha de corte. Una fecha límite unos días después de tu quincena hace que el dinero esté ahí cuando sale el pago.',
        },
      ],
    },
    {
      id: 'twocoins',
      title: 'Cómo lo hace TwoCoins',
      blocks: [
        {
          kind: 'p',
          content:
            'Registras una vez el día de corte y la fecha límite de cada tarjeta. TwoCoins marca la mejor tarjeta para hoy — la que pagas lo más tarde posible sin intereses —, cuenta lo que debe cada tarjeta contra lo que tienes, te muestra cada pago en su fecha límite junto a la quincena que lo cubre y lleva tu límite junto a tu saldo, para que veas cuánto de tu línea usas antes de que llegue el corte.',
        },
      ],
    },
  ],
};

const interestFreeInstalments: Guide = {
  id: 'interest-free-instalments',
  title: 'Meses sin intereses: cómo funcionan de verdad',
  intro:
    'Los meses sin intereses dividen una compra en mensualidades iguales, sin costo extra, siempre que pagues cada una a tiempo. El truco casi nunca está en los intereses: está en cuánto de tu línea de crédito ocupan y en cuántas mensualidades llevas a la vez.',
  summary:
    'Cuánto de tu línea ocupan los meses sin intereses, qué pasa cuando se acumulan o no pagas a tiempo, y cuándo conviene más pagar de contado.',
  published: PUBLISHED,
  updated: UPDATED,
  sections: [
    {
      id: 'how',
      title: 'Cómo funcionan',
      blocks: [
        {
          kind: 'p',
          content:
            'El precio se divide en mensualidades iguales, y cada estado de cuenta incluye una dentro del pago para no generar intereses. Si cubres ese pago completo cada mes, el precio no crece. La primera mensualidad suele aparecer en el estado de cuenta que sigue a la compra.',
        },
        {
          kind: 'p',
          content:
            'Lo que sorprende es la línea de crédito. El día que compras, el banco aparta el precio completo de tu crédito disponible y lo libera mensualidad por mensualidad: un celular de $12,000 a 12 meses ocupa $12,000 de tu línea hoy, y $1,000 menos cada mes.',
        },
      ],
    },
    {
      id: 'stacking',
      title: 'Dónde se complican: cuando se acumulan',
      blocks: [
        {
          kind: 'table',
          head: ['Compra', 'Precio', 'Meses', 'Al mes'],
          rows: [
            ['Pantalla', '$12,000', '12', '$1,000'],
            ['Celular', '$9,000', '18', '$500'],
            ['Vuelos', '$6,000', '6', '$1,000'],
            ['Refrigerador', '$15,000', '12', '$1,250'],
            ['Las cuatro, cada mes', '', '', '$3,750'],
          ],
        },
        {
          kind: 'p',
          content:
            'Ninguna es grande por sí sola. Juntas toman $3,750 de cada estado de cuenta durante seis meses, $2,750 los seis siguientes y $500 hasta que terminas el celular, antes de comprar cualquier otra cosa. Por quincena, son $1,875 que salen de cada pago en los primeros seis meses.',
        },
        {
          kind: 'p',
          content:
            'Cada plan nuevo se decide por su propia mensualidad; el total es lo que de verdad vas a sentir el próximo año.',
        },
      ],
    },
    {
      id: 'missed',
      title: 'Si no pagas a tiempo',
      blocks: [
        {
          kind: 'p',
          content:
            'La mensualidad es parte de tu pago para no generar intereses. Si pagas menos, pagas intereses sobre lo que queda pendiente, y según el contrato de tu banco, tus compras a meses pueden perder el beneficio y empezar a cobrar intereses. El contrato lo dice: léelo antes de firmar, no después.',
        },
      ],
    },
    {
      id: 'cash-price',
      title: 'Precio de contado contra precio a meses',
      blocks: [
        {
          kind: 'p',
          content:
            'Muchas tiendas tienen un precio de contado más bajo, o un descuento si pagas en una sola exhibición. Si el precio a meses es más alto, ya no es sin intereses: la diferencia es lo que cuesta. Compara los dos antes de elegir.',
        },
        {
          kind: 'p',
          content:
            'No los confundas con los meses con intereses, que cobran una tasa sobre cada mensualidad, ni con diferir una compra ya hecha desde la app del banco, que casi siempre cobra intereses o comisión.',
        },
      ],
    },
    {
      id: 'worth-it',
      title: 'Cuándo sí conviene',
      blocks: [
        {
          kind: 'ul',
          items: [
            'El precio a meses es igual al de contado. Si de contado es más barato, el plan tiene un costo: compáralos.',
            'Lo ibas a comprar de todos modos, y repartirlo te deja efectivo para el resto de la quincena.',
            'El total de todas tus mensualidades — no solo la de este plan — todavía deja espacio en cada quincena.',
            'Sabes en qué mes termina.',
          ],
        },
      ],
    },
    {
      id: 'twocoins',
      title: 'Cómo lo hace TwoCoins',
      blocks: [
        {
          kind: 'p',
          content:
            'TwoCoins guarda cada compra a meses sin intereses como lo que es: una mensualidad, con su número de pagos y el mes en que termina. Cuenta solo la mensualidad, en el ciclo en que se paga — nunca el precio completo el mes en que compraste —, y la descuenta de lo libre de esa quincena.',
        },
      ],
    },
  ],
};

export const guidesEs: Record<GuideId, Guide> = {
  'budget-by-paycheck': budgetByPaycheck,
  'statement-and-due-dates': statementAndDueDates,
  'interest-free-instalments': interestFreeInstalments,
};
